import JSZip from "jszip";
import { hasDataPage, qs } from "../core/dom";
import { refreshViwoSelects } from "../core/viwo-select";

const MAX_PARALLEL = 3;

const IMAGE_FORMATS = {
    SMART: "0",
    JPEG: "1",
    PNG: "2",
    WEBP: "3",
    GIF: "4",
    BMP: "5",
    TIFF: "6",
    AVIF: "7",
    HEIC: "8",
    ICO: "9",
    PDF: "10",
    SVG: "11"
};

const state = {
    items: [],
    filters: {
        search: "",
        status: "all"
    },
    queue: {
        running: false,
        paused: false,
        cancelPending: false,
        activeCount: 0
    },
    selectedItemId: null
};

export function initImageConverterPage() {
    if (!hasDataPage("image-converter")) return;

    const translations = getImageConverterTranslations();

    const page = {
        fileInput: qs("[data-file-input]"),
        dropzone: qs("[data-dropzone]"),
        fileList: qs("[data-file-list]"),
        emptyState: qs("[data-empty-state]"),
        selectFiles: qs("[data-select-files]"),
        clearFiles: qs("[data-clear-files]"),
        convertAll: qs("[data-convert-all]"),
        downloadAll: qs("[data-download-all]"),
        searchInput: qs("[data-search-input]"),
        statusPills: Array.from(document.querySelectorAll("[data-status-filter]")),
        quality: qs("[data-quality]"),
        compression: qs("[data-compression]"),
        blur: qs("[data-blur]"),
        brightness: qs("[data-brightness]"),
        contrast: qs("[data-contrast]"),
        sharpen: qs("[data-sharpen]"),
        rotate: qs("[data-rotate]"),
        grayscale: qs("[data-grayscale]"),
        invert: qs("[data-invert]"),
        flipHorizontal: qs("[data-flip-horizontal]"),
        flipVertical: qs("[data-flip-vertical]"),
        qualityValue: qs("[data-quality-value]"),
        compressionValue: qs("[data-compression-value]"),
        blurValue: qs("[data-blur-value]"),
        brightnessValue: qs("[data-brightness-value]"),
        contrastValue: qs("[data-contrast-value]"),
        sharpenValue: qs("[data-sharpen-value]"),
        width: qs("[data-width]"),
        height: qs("[data-height]"),
        preserveRatio: qs("[data-preserve-ratio]"),
        stripMetadata: qs("[data-strip-metadata]"),
        template: qs("#image-file-row-template"),
        summaryTotal: qs("[data-summary-total]"),
        summaryPending: qs("[data-summary-pending]"),
        summaryDone: qs("[data-summary-done]"),
        summaryError: qs("[data-summary-error]"),
        sizePresets: Array.from(document.querySelectorAll("[data-size-preset]")),
        filterPresets: Array.from(document.querySelectorAll("[data-filter-preset]")),
        previewModal: qs("[data-preview-modal]"),
        previewTitle: qs("[data-preview-title]"),
        previewSubtitle: qs("[data-preview-subtitle]"),
        previewOriginal: qs("[data-preview-original]"),
        previewConverted: qs("[data-preview-converted]"),
        previewMeta: qs("[data-preview-meta]"),
        previewCloseButtons: Array.from(document.querySelectorAll("[data-preview-close]")),
        pauseQueue: qs("[data-pause-queue]"),
        resumeQueue: qs("[data-resume-queue]"),
        retryFailed: qs("[data-retry-failed]"),
        cancelPending: qs("[data-cancel-pending]"),
        selectedPreview: qs("[data-selected-preview]"),
        selectedPreviewImage: qs("[data-selected-preview-image]"),
        selectedPreviewTitle: qs("[data-selected-preview-title]"),
        selectedPreviewSubtitle: qs("[data-selected-preview-subtitle]"),
        selectedPreviewBadge: qs("[data-selected-preview-badge]"),
        selectedPreviewMeta: qs("[data-selected-preview-meta]"),
        preferModernFormats: qs("[data-prefer-modern-formats]"),
        keepTransparency: qs("[data-keep-transparency]"),
        autoOrient: qs("[data-auto-orient]"),

        svgOptions: qs("[data-svg-options]"),
        svgDpi: qs("[data-svg-dpi]"),
        svgOutputCard: qs("[data-svg-output-card]"),

        icoSettings: document.querySelector("[data-ico-settings]"),
        iconMultiSize: document.querySelector("[data-icon-multisize]"),
        iconSizes: document.querySelector("[data-icon-sizes]"),
        iconFitMode: document.querySelector("[data-icon-fit-mode]"),
        iconTransparent: document.querySelector("[data-icon-transparent]"),

        translations: getImageConverterTranslations()
    };

    bindQueueControls(page);
    //renderSelectedPreview(page);
    bindPreviewModal(page);
    bindRangeLabels(page);
    bindDropzone(page);
    bindToolbar(page);
    bindFilters(page);
    bindPresets(page);
    renderList(page);
    updateSummary(page);
    applySeoDefaultFormat(page);
    updateSvgUiState(page);
    updateFormatSpecificPanels(page);
}

function updateFormatSpecificPanels(page) {
    const selectedFormat = getSelectedFormat();

    if (page.icoSettings) {
        page.icoSettings.hidden = selectedFormat !== IMAGE_FORMATS.ICO;
    }
}

function bindQueueControls(page) {
    page.convertAll?.addEventListener("click", async () => {
        const pending = getVisibleItems().filter(x => !x.isProcessing && !x.result);
        if (!pending.length) return;

        state.queue.running = true;
        state.queue.paused = false;
        state.queue.cancelPending = false;

        await runManagedQueue(pending, MAX_PARALLEL, page);
    });

    page.pauseQueue?.addEventListener("click", () => {
        state.queue.paused = true;
    });

    page.resumeQueue?.addEventListener("click", async () => {
        if (!state.queue.running) {
            const pending = state.items.filter(x => !x.isProcessing && !x.result && !x.isError);
            if (!pending.length) return;

            state.queue.running = true;
            state.queue.paused = false;
            state.queue.cancelPending = false;

            await runManagedQueue(pending, MAX_PARALLEL, page);
            return;
        }

        state.queue.paused = false;
    });

    page.retryFailed?.addEventListener("click", async () => {
        const failed = state.items.filter(x => x.isError && !x.isProcessing);
        if (!failed.length) return;

        failed.forEach(x => {
            x.isError = false;
            x.status = "Bekliyor";
            x.progress = 0;
        });

        renderList(page);

        state.queue.running = true;
        state.queue.paused = false;
        state.queue.cancelPending = false;

        await runManagedQueue(failed, MAX_PARALLEL, page);
    });

    page.cancelPending?.addEventListener("click", () => {
        state.queue.cancelPending = true;
        state.queue.paused = false;

        state.items
            .filter(x => !x.isProcessing && !x.result)
            .forEach(x => {
                x.status = "Cancelled";
                x.progress = 0;
            });

        updateSummary(page);
        renderList(page);
    });
}

function paintRangeProgress(input) {
    if (!input) return;

    const min = Number(input.min || 0);
    const max = Number(input.max || 100);
    const value = Number(input.value || 0);
    const percent = ((value - min) * 100) / (max - min);

    input.style.setProperty("--range-percent", `${percent}%`);
}

function bindRangeLabels(page) {
    const rangePairs = [
        [page.quality, page.qualityValue],
        [page.compression, page.compressionValue],
        [page.blur, page.blurValue],
        [page.brightness, page.brightnessValue],
        [page.contrast, page.contrastValue],
        [page.sharpen, page.sharpenValue]
    ];

    rangePairs.forEach(([input, output]) => {
        if (!input || !output) return;

        output.textContent = input.value;
        paintRangeProgress(input);

        input.addEventListener("input", () => {
            output.textContent = input.value;
            paintRangeProgress(input);
        });
    });

    [
        page.quality,
        page.compression,
        page.blur,
        page.brightness,
        page.contrast,
        page.sharpen,
        page.rotate,
        page.width,
        page.height,
        page.preserveRatio,
        page.stripMetadata,
        page.grayscale,
        page.invert,
        page.flipHorizontal,
        page.flipVertical
    ].forEach(el => {
        if (!el) return;

        const eventName = el.type === "checkbox" || el.tagName === "SELECT" ? "change" : "input";
        el.addEventListener(eventName, () => renderSelectedPreview(page));
    });

    document.querySelectorAll('input[name="outputFormat"]').forEach(radio => {
        radio.addEventListener("change", () => {
            updateSvgUiState(page);
            updateFormatSpecificPanels(page);
            renderSelectedPreview(page);
            renderList(page);
        });
    });
}

function updateRowSvgOptions(item) {
    const row = item.row;
    if (!row) return;

    const svgOption = row.querySelector("[data-svg-only-option]");
    if (!svgOption) return;

    const isSvg = isSvgFile(item.file);

    svgOption.disabled = !isSvg;
    svgOption.hidden = !isSvg;

    const select = row.querySelector("[data-override-format]");
    if (select && !isSvg && select.value === IMAGE_FORMATS.SVG) {
        select.value = "";
        item.override.format = "";
    }
}

function bindDropzone(page) {
    page.dropzone?.addEventListener("dragover", (e) => {
        e.preventDefault();
        page.dropzone.classList.add("is-dragover");
    });

    page.dropzone?.addEventListener("dragleave", () => {
        page.dropzone.classList.remove("is-dragover");
    });

    page.dropzone?.addEventListener("drop", (e) => {
        e.preventDefault();
        page.dropzone.classList.remove("is-dragover");
        addFiles(Array.from(e.dataTransfer.files || []), page);
    });

    page.fileInput?.addEventListener("change", () => {
        addFiles(Array.from(page.fileInput.files || []), page);
        page.fileInput.value = "";
    });
}

function bindToolbar(page) {
    page.selectFiles?.addEventListener("click", () => page.fileInput?.click());

    page.clearFiles?.addEventListener("click", () => {
        state.items.forEach(x => URL.revokeObjectURL(x.previewUrl));
        state.items = [];
        state.selectedItemId = null;
        renderList(page);
        renderSelectedPreview(page);
        updateDownloadAllState(page);
        updateSummary(page);
        updateSvgUiState(page);
    });

    page.downloadAll?.addEventListener("click", async () => {
        const converted = state.items.filter(x => x.result?.blob);
        if (!converted.length) return;

        const zip = new JSZip();
        converted.forEach(item => zip.file(item.result.fileName, item.result.blob));

        const zipBlob = await zip.generateAsync({ type: "blob" });
        triggerDownload(zipBlob, "converted-images.zip");
    });
}

function selectItem(itemId, page) {
    state.selectedItemId = itemId;
    renderSelectedPreview(page);

    state.items.forEach(item => {
        if (item.row) {
            item.row.classList.toggle("is-selected", item.id === itemId);
        }
    });
}

function getSelectedItem() {
    return state.items.find(x => x.id === state.selectedItemId) || null;
}

function renderSelectedPreview(page) {
    const item = getSelectedItem();
    return;

    if (!item) {
        page.selectedPreviewTitle.textContent = "No image selected";
        page.selectedPreviewSubtitle.textContent = "Bir satır seçildiğinde burada canlı önizleme görünür.";
        page.selectedPreviewImage.removeAttribute("src");
        page.selectedPreviewBadge.textContent = "Preview";
        page.selectedPreviewMeta.innerHTML = `
            <div class="preview-meta__item">
                <strong>Status</strong>
                <span>Waiting</span>
            </div>
        `;
        return;
    }

    page.selectedPreviewTitle.textContent = item.file.name;
    page.selectedPreviewSubtitle.textContent = item.dimensions || formatBytes(item.file.size);
    page.selectedPreviewBadge.textContent = resolveStateBadge(item);
    page.selectedPreviewImage.src = item.previewUrl;

    const effective = resolveEffectiveSettings(item, page);

    page.selectedPreviewMeta.innerHTML = `
        <div class="preview-meta__item"><strong>Format</strong><span>${mapFormatLabel(effective.format)}</span></div>
        <div class="preview-meta__item"><strong>Quality</strong><span>${effective.quality}</span></div>
        <div class="preview-meta__item"><strong>Resize</strong><span>${effective.width || "Auto"} x ${effective.height || "Auto"}</span></div>
        <div class="preview-meta__item"><strong>Filters</strong><span>${summarizeFilters(effective)}</span></div>
    `;
}

function mapFormatLabel(value) {
    return ({
        "0": "SMART",
        "1": "JPG",
        "2": "PNG",
        "3": "WEBP",
        "4": "GIF",
        "5": "BMP",
        "6": "TIFF",
        "7": "AVIF",
        "8": "HEIC",
        "9": "ICO",
        "10": "PDF",
        "11": "SVG"
    })[String(value)] || "SMART";
}

function summarizeFilters(settings) {
    const active = [];

    if (settings.grayscale) active.push("Grayscale");
    if (settings.invert) active.push("Invert");
    if (Number(settings.blur) > 0) active.push(`Blur ${settings.blur}`);
    if (Number(settings.brightness) !== 0) active.push(`Brightness ${settings.brightness}`);
    if (Number(settings.contrast) !== 0) active.push(`Contrast ${settings.contrast}`);
    if (Number(settings.sharpen) > 0) active.push(`Sharpen ${settings.sharpen}`);
    if (Number(settings.rotate) > 0) active.push(`Rotate ${settings.rotate}°`);
    if (settings.flipHorizontal) active.push("Flip H");
    if (settings.flipVertical) active.push("Flip V");

    return active.length ? active.join(", ") : "None";
}

async function runManagedQueue(items, concurrency, page) {
    let currentIndex = 0;

    async function worker() {
        while (currentIndex < items.length) {
            if (state.queue.cancelPending) {
                break;
            }

            while (state.queue.paused) {
                await delay(200);
                if (state.queue.cancelPending) return;
            }

            const index = currentIndex++;
            const item = items[index];

            if (!item || item.result || item.isProcessing) {
                continue;
            }

            await convertItem(item, page);
        }
    }

    const workers = Array.from(
        { length: Math.min(concurrency, items.length) },
        () => worker()
    );

    await Promise.all(workers);

    state.queue.running = false;
    state.queue.paused = false;
    state.queue.cancelPending = false;
}

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

async function runParallelQueue(items, concurrency, worker) {
    let currentIndex = 0;

    async function runner() {
        while (currentIndex < items.length) {
            const index = currentIndex++;
            const item = items[index];
            await worker(item);
        }
    }

    const runners = Array.from({ length: Math.min(concurrency, items.length) }, () => runner());
    await Promise.all(runners);
}

function bindFilters(page) {
    page.searchInput?.addEventListener("input", () => {
        state.filters.search = page.searchInput.value.trim().toLowerCase();
        renderList(page);
    });

    page.statusPills.forEach(btn => {
        btn.addEventListener("click", () => {
            state.filters.status = btn.dataset.statusFilter || "all";
            page.statusPills.forEach(x => x.classList.remove("active"));
            btn.classList.add("active");
            renderList(page);
        });
    });
}

function bindPresets(page) {
    page.sizePresets.forEach(btn => {
        btn.addEventListener("click", () => {
            page.sizePresets.forEach(x => x.classList.remove("active"));
            btn.classList.add("active");

            const widthValue = btn.dataset.widthValue ?? "";
            const heightValue = btn.dataset.heightValue ?? "";

            if (widthValue === "-1" && heightValue === "-1") {
                page.width.focus();
                return;
            }

            page.width.value = widthValue;
            page.height.value = heightValue;
        });
    });

    page.filterPresets.forEach(btn => {
        btn.addEventListener("click", () => {
            applyFilterPreset(btn.dataset.filterPreset || "", page);
        });
    });
}

function applyFilterPreset(preset, page) {
    resetFilterAdjustments(page);

    switch (preset) {
        case "bw":
            page.grayscale.checked = true;
            page.contrast.value = "8";
            break;
        case "soft":
            page.blur.value = "1.4";
            page.brightness.value = "4";
            break;
        case "sharp":
            page.sharpen.value = "2";
            page.contrast.value = "10";
            break;
        case "dramatic":
            page.contrast.value = "18";
            page.sharpen.value = "1.4";
            break;
        case "clean":
            page.sharpen.value = "1";
            page.brightness.value = "2";
            break;
    }

    page.blurValue.textContent = page.blur.value;
    page.brightnessValue.textContent = page.brightness.value;
    page.contrastValue.textContent = page.contrast.value;
    page.sharpenValue.textContent = page.sharpen.value;
}

function resetFilterAdjustments(page) {
    page.grayscale.checked = false;
    page.invert.checked = false;
    page.blur.value = "0";
    page.brightness.value = "0";
    page.contrast.value = "0";
    page.sharpen.value = "0";
}

function addFiles(files, page) {
    const imageFiles = files.filter(isSupportedImageFile);

    for (const file of imageFiles) {
        const id = crypto.randomUUID();
        const item = {
            id,
            file,
            previewUrl: URL.createObjectURL(file),
            status: "Bekliyor",
            progress: 0,
            isProcessing: false,
            isError: false,
            result: null,
            row: null,
            dimensions: "",
            override: {
                enabled: false,
                format: "",
                quality: "",
                width: "",
                height: "",
                blur: "",
                brightness: "",
                contrast: "",
                sharpen: "",
                rotate: "",
                grayscale: false,
                invert: false,
                flipHorizontal: false,
                flipVertical: false
            }
        };

        state.items.push(item);
        resolveImageDimensions(item, page);
    }

    renderList(page);
    updateSummary(page);
    updateSvgUiState(page);
    renderSelectedPreview(page);
}

function resolveImageDimensions(item, page) {
    const img = new Image();

    img.onload = () => {
        const width = img.naturalWidth || 0;
        const height = img.naturalHeight || 0;

        if ((!width || !height) && isSvgFile(item.file)) {
            item.dimensions = "SVG vector";
        } else if (!width || !height) {
            item.dimensions = "Unknown size";
        } else {
            item.dimensions = `${width}×${height}`;
        }

        renderList(page);
        renderSelectedPreview(page);
    };

    img.onerror = () => {
        item.dimensions = isSvgFile(item.file) ? "SVG vector" : "Unknown size";
        renderList(page);
        renderSelectedPreview(page);
    };

    img.src = item.previewUrl;
}

function formatOutputDimensions(result) {
    const width = Number(result.outputWidth || 0);
    const height = Number(result.outputHeight || 0);

    if (!width || !height) {
        if (String(result.resolvedFormat || "").toLowerCase() === "svg") {
            return "SVG vector";
        }

        return "Unknown size";
    }

    return `${width}×${height}`;
}

function renderList(page) {
    page.fileList.querySelectorAll("[data-file-row]").forEach(x => x.remove());

    const visibleItems = getVisibleItems();

    if (!visibleItems.length) {
        page.emptyState.hidden = false;
        return;
    }

    page.emptyState.hidden = true;

    for (const item of visibleItems) {
        const node = createRow(item, page);
        page.fileList.appendChild(node);
    }

    updateSummary(page);
}

function getVisibleItems() {
    return state.items.filter(item => {
        const searchOk = !state.filters.search || item.file.name.toLowerCase().includes(state.filters.search);
        const statusOk = matchStatusFilter(item, state.filters.status);
        return searchOk && statusOk;
    });
}

function matchStatusFilter(item, filter) {
    switch (filter) {
        case "pending":
            return !item.result && !item.isProcessing && !item.isError;
        case "processing":
            return item.isProcessing;
        case "done":
            return !!item.result;
        case "error":
            return item.isError;
        default:
            return true;
    }
}

function createRow(item, page) {
    const wrapper = document.createElement("div");
    const template = page.template.innerHTML.trim();
    const html = applyTemplateTranslations(template, page.translations);
    wrapper.innerHTML = html;

    const row = wrapper.firstElementChild;
    item.row = row;

    const img = row.querySelector("img");
    const name = row.querySelector("[data-name]");
    const size = row.querySelector("[data-size]");
    const dimensions = row.querySelector("[data-dimensions]");
    const status = row.querySelector("[data-status]");
    const progressBar = row.querySelector("[data-progress-bar]");
    const progressText = row.querySelector("[data-progress-text]");
    const resultMeta = row.querySelector("[data-result-meta]");
    const convertBtn = row.querySelector("[data-convert-item]");
    const downloadBtn = row.querySelector("[data-download-item]");
    const removeBtn = row.querySelector("[data-remove-item]");
    const formatBadge = row.querySelector("[data-format-badge]");
    const stateBadge = row.querySelector("[data-state-badge]");
    const previewBtn = row.querySelector("[data-preview-item]");
    const overrideToggle = row.querySelector("[data-override-toggle]");
    const overrideBody = row.querySelector("[data-override-body]");
    const overrideEnabled = row.querySelector("[data-override-enabled]");
    const overrideFormat = row.querySelector("[data-override-format]");
    const overrideQuality = row.querySelector("[data-override-quality]");
    const overrideWidth = row.querySelector("[data-override-width]");
    const overrideHeight = row.querySelector("[data-override-height]");
    const overrideBlur = row.querySelector("[data-override-blur]");
    const overrideBrightness = row.querySelector("[data-override-brightness]");
    const overrideContrast = row.querySelector("[data-override-contrast]");
    const overrideSharpen = row.querySelector("[data-override-sharpen]");
    const overrideRotate = row.querySelector("[data-override-rotate]");
    const overrideGrayscale = row.querySelector("[data-override-grayscale]");
    const overrideInvert = row.querySelector("[data-override-invert]");
    const overrideFlipHorizontal = row.querySelector("[data-override-flip-horizontal]");
    const overrideFlipVertical = row.querySelector("[data-override-flip-vertical]");

    img.src = item.previewUrl;
    img.alt = item.file.name;
    name.textContent = item.file.name;
    size.textContent = formatBytes(item.file.size);
    dimensions.textContent = item.dimensions || "Resolving...";
    status.textContent = item.status;
    progressBar.style.width = `${item.progress}%`;
    progressText.textContent = `${item.progress}%`;
    overrideEnabled.checked = item.override.enabled;
    overrideFormat.value = item.override.format;
    overrideQuality.value = item.override.quality;
    overrideWidth.value = item.override.width;
    overrideHeight.value = item.override.height;
    overrideBlur.value = item.override.blur;
    overrideBrightness.value = item.override.brightness;
    overrideContrast.value = item.override.contrast;
    overrideSharpen.value = item.override.sharpen;
    overrideRotate.value = item.override.rotate;
    overrideGrayscale.checked = item.override.grayscale;
    overrideInvert.checked = item.override.invert;
    overrideFlipHorizontal.checked = item.override.flipHorizontal;
    overrideFlipVertical.checked = item.override.flipVertical;

    updateRowSvgOptions(item);

    overrideToggle.addEventListener("click", () => {
        const isHidden = overrideBody.hasAttribute("hidden");
        if (isHidden) {
            overrideBody.removeAttribute("hidden");
            overrideToggle.classList.add("is-open");
        } else {
            overrideBody.setAttribute("hidden", "");
            overrideToggle.classList.remove("is-open");
        }
    });

    bindOverrideField(overrideEnabled, "change", item, "enabled", "checked", page);
    bindOverrideField(overrideFormat, "change", item, "format", "value", page);
    bindOverrideField(overrideQuality, "input", item, "quality", "value", page);
    bindOverrideField(overrideWidth, "input", item, "width", "value", page);
    bindOverrideField(overrideHeight, "input", item, "height", "value", page);
    bindOverrideField(overrideBlur, "input", item, "blur", "value", page);
    bindOverrideField(overrideBrightness, "input", item, "brightness", "value", page);
    bindOverrideField(overrideContrast, "input", item, "contrast", "value", page);
    bindOverrideField(overrideSharpen, "input", item, "sharpen", "value", page);
    bindOverrideField(overrideRotate, "change", item, "rotate", "value", page);
    bindOverrideField(overrideGrayscale, "change", item, "grayscale", "checked", page);
    bindOverrideField(overrideInvert, "change", item, "invert", "checked", page);
    bindOverrideField(overrideFlipHorizontal, "change", item, "flipHorizontal", "checked", page);
    bindOverrideField(overrideFlipVertical, "change", item, "flipVertical", "checked", page);

    formatBadge.textContent = mapFormatLabel(resolveEffectiveSettings(item, page).format);
    stateBadge.textContent = resolveStateBadge(item);

    if (item.result) {
        downloadBtn.disabled = false;
        resultMeta.innerHTML = buildResultMeta(item.result);
    } else {
        downloadBtn.disabled = true;
        resultMeta.textContent = "";
    }

    convertBtn.addEventListener("click", async () => {
        await convertItem(item, page);
    });

    downloadBtn.addEventListener("click", () => {
        if (!item.result?.blob) return;
        triggerDownload(item.result.blob, item.result.fileName);
    });

    previewBtn.addEventListener("click", () => {
        selectItem(item.id, page);
        openPreviewModal(item, page);
    });

    removeBtn.addEventListener("click", () => {
        URL.revokeObjectURL(item.previewUrl);
        state.items = state.items.filter(x => x.id !== item.id);

        if (state.selectedItemId === item.id) {
            state.selectedItemId = state.items[0]?.id || null;
        }

        renderList(page);
        renderSelectedPreview(page);
        updateDownloadAllState(page);
        updateSummary(page);
        updateSvgUiState(page);
    });

    row.addEventListener("click", (e) => {
        const interactive = e.target.closest("button, input, select, label");
        if (interactive) return;

        selectItem(item.id, page);
    });

    if (!state.selectedItemId) {
        state.selectedItemId = item.id;
    }
    row.classList.toggle("is-selected", item.id === state.selectedItemId);

    refreshViwoSelects(row);
    return row;
}

function bindOverrideField(element, eventName, item, fieldName, mode = "value", page = null) {
    if (!element) return;

    element.addEventListener(eventName, () => {
        item.override[fieldName] = mode === "checked" ? element.checked : element.value;

        if (page && state.selectedItemId === item.id) {
            renderSelectedPreview(page);
        }
    });
}

async function convertItem(item, page) {
    if (item.isProcessing) return;

    item.isProcessing = true;
    item.isError = false;
    updateRowState(item, {
        status: "Yükleniyor",
        progress: 0
    });

    let formData;

    try {
        formData = buildFormData(item, page);
    } catch (error) {
        updateRowState(item, {
            status: error?.message || "Invalid settings",
            progress: 100,
            isError: true
        });

        item.isProcessing = false;
        updateSummary(page);
        return;
    }

    try {
        const convertUrl =
            document.querySelector("[data-image-convert-url]")?.value || "/tools/image-converter/convert";
        const response = await uploadWithProgress(convertUrl, formData, (percent) => {
            updateRowState(item, {
                status: "Yükleniyor",
                progress: Math.min(percent, 85)
            });
        });

        updateRowState(item, {
            status: "İşleniyor",
            progress: 92
        });

        const result = JSON.parse(response);

        if (!result.success) {
            throw new Error(result.message || "Dönüştürme başarısız.");
        }

        const blob = base64ToBlob(result.outputBase64, result.outputContentType);

        item.result = {
            blob,
            fileName: result.outputFileName,
            size: result.convertedSize,
            originalSize: result.originalSize,
            sizeChangePercent: result.sizeChangePercent,
            outputWidth: result.outputWidth,
            outputHeight: result.outputHeight,
            resolvedFormat: result.resolvedFormat
        };

        updateRowState(item, {
            status: "Tamamlandı",
            progress: 100
        });

        refreshRow(item);
        updateDownloadAllState(page);
    } catch (error) {
        updateRowState(item, {
            status: error?.message || "Hata oluştu",
            progress: 100,
            isError: true
        });
    } finally {
        item.isProcessing = false;
        updateSummary(page);
        renderList(page);
    }
}

function buildFormData(item, page) {
    const effective = resolveEffectiveSettings(item, page);

    if (effective.format === IMAGE_FORMATS.SVG && !isSvgFile(item.file)) {
        throw new Error("SVG output is only available for SVG source files.");
    }

    const formData = new FormData();

    formData.append("file", item.file);
    formData.append("Format", effective.format);
    formData.append("Quality", effective.quality);
    formData.append("CompressionLevel", effective.compressionLevel);

    if (effective.width) formData.append("Width", effective.width);
    if (effective.height) formData.append("Height", effective.height);

    formData.append("PreserveAspectRatio", effective.preserveAspectRatio);
    formData.append("StripMetadata", effective.stripMetadata);
    formData.append("AutoOrient", effective.autoOrient);

    formData.append("Grayscale", effective.grayscale);
    formData.append("Invert", effective.invert);
    formData.append("Blur", effective.blur);
    formData.append("Brightness", effective.brightness);
    formData.append("Contrast", effective.contrast);
    formData.append("Sharpen", effective.sharpen);
    formData.append("Rotate", effective.rotate);
    formData.append("FlipHorizontal", effective.flipHorizontal);
    formData.append("FlipVertical", effective.flipVertical);

    formData.append("PreferModernFormats", effective.preferModernFormats);
    formData.append("KeepTransparencyWhenPossible", effective.keepTransparencyWhenPossible);

    formData.append("IconMultiSize", page.iconMultiSize?.checked ? "true" : "false");
    formData.append("IconSizes", page.iconSizes?.value || "16,24,32,48,64,128,256");
    formData.append("IconFitMode", page.iconFitMode?.value || "contain");
    formData.append("IconTransparentBackground", page.iconTransparent?.checked ? "true" : "false");

    formData.append("SvgDpi", page.svgDpi?.value || "300");

    return formData;
}

function resolveEffectiveSettings(item, page) {
    const globalSettings = {
        format: getSelectedFormat(),
        quality: page.quality.value,
        compressionLevel: page.compression.value,
        width: page.width.value.trim(),
        height: page.height.value.trim(),
        preserveAspectRatio: page.preserveRatio.checked,
        stripMetadata: page.stripMetadata.checked,
        grayscale: page.grayscale.checked,
        invert: page.invert.checked,
        blur: page.blur.value,
        brightness: page.brightness.value,
        contrast: page.contrast.value,
        sharpen: page.sharpen.value,
        rotate: page.rotate.value,
        flipHorizontal: page.flipHorizontal.checked,
        flipVertical: page.flipVertical.checked,
        autoOrient: page.autoOrient.checked,
        preferModernFormats: page.preferModernFormats.checked,
        keepTransparencyWhenPossible: page.keepTransparency.checked,
    };

    if (!item.override.enabled) {
        return globalSettings;
    }

    return {
        ...globalSettings,
        format: item.override.format || globalSettings.format,
        quality: item.override.quality || globalSettings.quality,
        width: item.override.width || globalSettings.width,
        height: item.override.height || globalSettings.height,
        blur: item.override.blur || globalSettings.blur,
        brightness: item.override.brightness || globalSettings.brightness,
        contrast: item.override.contrast || globalSettings.contrast,
        sharpen: item.override.sharpen || globalSettings.sharpen,
        rotate: item.override.rotate || globalSettings.rotate,
        grayscale: item.override.grayscale || globalSettings.grayscale,
        invert: item.override.invert || globalSettings.invert,
        flipHorizontal: item.override.flipHorizontal || globalSettings.flipHorizontal,
        flipVertical: item.override.flipVertical || globalSettings.flipVertical
    };
}

function bindPreviewModal(page) {
    page.previewCloseButtons.forEach(btn => {
        btn.addEventListener("click", () => closePreviewModal(page));
    });

    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
            closePreviewModal(page);
        }
    });
}

function openPreviewModal(item, page) {
    page.previewTitle.textContent = item.file.name;
    page.previewSubtitle.textContent = item.dimensions || formatBytes(item.file.size);
    page.previewOriginal.src = item.previewUrl;

    if (item.result?.blob) {
        const convertedUrl = URL.createObjectURL(item.result.blob);
        page.previewConverted.src = convertedUrl;
        page.previewConverted.dataset.objectUrl = convertedUrl;

        page.previewMeta.innerHTML = `
        <div class="preview-meta__item"><strong>Original</strong><span>${formatBytes(item.file.size)}</span></div>
        <div class="preview-meta__item"><strong>Converted</strong><span>${formatBytes(item.result.size)}</span></div>
        <div class="preview-meta__item"><strong>Output Size</strong><span>${formatOutputDimensions(item.result)}</span></div>
        <div class="preview-meta__item"><strong>Difference</strong><span>${formatPercent(item.result.sizeChangePercent)}</span></div>
    `;
    } else {
        page.previewConverted.removeAttribute("src");
        page.previewMeta.innerHTML = `
        <div class="preview-meta__item"><strong>Status</strong><span>Henüz dönüştürülmedi</span></div>
        <div class="preview-meta__item"><strong>Live Settings</strong><span>${summarizeFilters(resolveEffectiveSettings(item, page))}</span></div>
    `;
    }

    page.previewModal.hidden = false;
    document.body.classList.add("modal-open");
}

function applySeoDefaultFormat(page) {
    const seoTarget = qs("[data-seo-target-format]");
    if (!seoTarget) return;

    const target = (seoTarget.value || "").trim().toLowerCase();
    const map = {
        "jpg": "1",
        "jpeg": "1",
        "png": "2",
        "webp": "3",
        "tiff": "6",
        "avif": "7",
        "heic": "8",
        "ico": "9",
        "pdf": "10",
        "svg": "11"
    };

    const value = map[target];
    if (!value) return;

    const radio = document.querySelector(`input[name="outputFormat"][value="${value}"]`);
    if (!radio) return;

    radio.checked = true;
    updateSvgUiState(page);
    updateFormatSpecificPanels(page);
    renderSelectedPreview(page);
}

function closePreviewModal(page) {
    if (page.previewConverted?.dataset.objectUrl) {
        URL.revokeObjectURL(page.previewConverted.dataset.objectUrl);
        delete page.previewConverted.dataset.objectUrl;
    }

    page.previewModal.hidden = true;
    document.body.classList.remove("modal-open");
}

function updateRowState(item, patch) {
    if (typeof patch.status !== "undefined") item.status = patch.status;
    if (typeof patch.progress !== "undefined") item.progress = patch.progress;
    if (typeof patch.isError !== "undefined") item.isError = patch.isError;

    refreshRow(item);
}

function refreshRow(item, page = null) {
    if (!item.row) return;

    const status = item.row.querySelector("[data-status]");
    const progressBar = item.row.querySelector("[data-progress-bar]");
    const progressText = item.row.querySelector("[data-progress-text]");
    const resultMeta = item.row.querySelector("[data-result-meta]");
    const downloadBtn = item.row.querySelector("[data-download-item]");
    const stateBadge = item.row.querySelector("[data-state-badge]");
    const formatBadge = item.row.querySelector("[data-format-badge]");

    status.textContent = item.status;
    progressBar.style.width = `${item.progress}%`;
    progressText.textContent = `${item.progress}%`;
    stateBadge.textContent = resolveStateBadge(item);
    formatBadge.textContent = page
        ? mapFormatLabel(resolveEffectiveSettings(item, page).format)
        : getSelectedFormatLabel();

    item.row.classList.toggle("is-error", !!item.isError);
    item.row.classList.toggle("is-success", item.status === "Tamamlandı");

    if (item.result) {
        downloadBtn.disabled = false;
        resultMeta.innerHTML = buildResultMeta(item.result);
    }
}

function buildResultMeta(result) {
    const changeText = formatPercent(result.sizeChangePercent);
    const changeClass = result.sizeChangePercent <= 0 ? "is-good" : "is-bad";

    return `
        <span>Output: <strong>${result.fileName}</strong></span>
        <span class="dot"></span>
        <span>Format: ${result.resolvedFormat}</span>
        <span class="dot"></span>
        <span>${formatOutputDimensions(result)}</span>
        <span class="dot"></span>
        <span>New: ${formatBytes(result.size)}</span>
        <span class="dot"></span>
        <span>Old: ${formatBytes(result.originalSize)}</span>
        <span class="dot"></span>
        <span class="${changeClass}">${changeText}</span>
    `;
}

function resolveStateBadge(item) {
    if (item.isError) return "Error";
    if (item.isProcessing) return "Processing";
    if (item.result) return "Done";
    return "Pending";
}

function updateDownloadAllState(page) {
    const hasAny = state.items.some(x => x.result?.blob);
    page.downloadAll.disabled = !hasAny;
}

function updateSummary(page) {
    page.summaryTotal.textContent = state.items.length.toString();
    page.summaryPending.textContent = state.items.filter(x => !x.result && !x.isProcessing && !x.isError).length.toString();
    page.summaryDone.textContent = state.items.filter(x => !!x.result).length.toString();
    page.summaryError.textContent = state.items.filter(x => x.isError).length.toString();
}

function updateSvgUiState(page) {
    const hasSvg = hasAnySvgFile();
    const onlySvg = hasOnlySvgFiles();

    if (page.svgOptions) {
        page.svgOptions.hidden = !hasSvg;
    }

    const svgCard = page.svgOutputCard;
    const svgRadio = svgCard?.querySelector('input[name="outputFormat"]');

    if (!svgCard || !svgRadio) return;

    svgCard.classList.toggle("is-disabled", !onlySvg);
    svgRadio.disabled = !onlySvg;

    if (!onlySvg && svgRadio.checked) {
        const smartRadio = document.querySelector(`input[name="outputFormat"][value="${IMAGE_FORMATS.SMART}"]`);
        if (smartRadio) {
            smartRadio.checked = true;
        }
    }
}

function getSelectedFormat() {
    const checked = document.querySelector('input[name="outputFormat"]:checked');
    return checked ? checked.value : "3";
}

function getSelectedFormatLabel() {
    return ({
        "0": "SMART",
        "1": "JPG",
        "2": "PNG",
        "3": "WEBP",
        "4": "GIF",
        "5": "BMP",
        "6": "TIFF",
        "7": "AVIF",
        "8": "HEIC",
        "9": "ICO",
        "10": "PDF",
        "11": "SVG"
    })[getSelectedFormat()] || "SMART";
}

function isSvgFile(file) {
    const name = file?.name?.toLowerCase() || "";
    const type = file?.type?.toLowerCase() || "";

    return type === "image/svg+xml" || name.endsWith(".svg");
}

function hasAnySvgFile() {
    return state.items.some(item => isSvgFile(item.file));
}

function hasOnlySvgFiles() {
    return state.items.length > 0 && state.items.every(item => isSvgFile(item.file));
}

function isSupportedImageFile(file) {
    return /\.(jpg|jpeg|png|webp|gif|bmp|tif|tiff|heic|heif|avif|pdf|ico|svg)$/i.test(file.name);
}

function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return "-";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let value = bytes;

    while (value >= 1024 && i < units.length - 1) {
        value /= 1024;
        i++;
    }

    return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatPercent(value) {
    if (typeof value !== "number") return "-";
    if (value > 0) return `+%${Math.abs(value).toFixed(2)}`;
    if (value < 0) return `-%${Math.abs(value).toFixed(2)}`;
    return "%0";
}

function triggerDownload(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function base64ToBlob(base64, contentType) {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);

    for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
    }

    return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
}

function uploadWithProgress(url, formData, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url, true);

        const tokenEl = document.querySelector("[data-request-verification-token]");
        const token = tokenEl?.value;

        if (token) {
            xhr.setRequestHeader("RequestVerificationToken", token);
        }

        xhr.upload.onprogress = (e) => {
            if (!e.lengthComputable) return;
            const percent = Math.round((e.loaded / e.total) * 100);
            onProgress?.(percent);
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(xhr.responseText);
                return;
            }

            try {
                const json = JSON.parse(xhr.responseText);
                reject(new Error(json.message || "İstek başarısız."));
            } catch {
                reject(new Error("İstek başarısız."));
            }
        };

        xhr.onerror = () => reject(new Error("Ağ hatası oluştu."));
        xhr.send(formData);
    });
}

function getImageConverterTranslations() {
    const el = document.getElementById("image-converter-i18n");
    if (!el) return {};

    try {
        return JSON.parse(el.textContent || "{}");
    } catch {
        return {};
    }
}

function applyTemplateTranslations(template, translations) {
    const map = {
        "{{ROW_WAITING}}": translations.rowWaiting ?? "Waiting",
        "{{ROW_PREVIEW}}": translations.rowPreview ?? "Preview",
        "{{ROW_CONVERT}}": translations.rowConvert ?? "Convert",
        "{{ROW_DOWNLOAD}}": translations.rowDownload ?? "Download",
        "{{ROW_REMOVE}}": translations.rowRemove ?? "Remove",
        "{{ROW_PENDING}}": translations.rowPending ?? "Pending",
        "{{ROW_OVERRIDE_TITLE}}": translations.rowOverrideTitle ?? "Override Settings",
        "{{ROW_USE_CUSTOM}}": translations.rowUseCustom ?? "Use custom settings for this row",
        "{{ROW_FORMAT}}": translations.rowFormat ?? "Format",
        "{{ROW_QUALITY}}": translations.rowQuality ?? "Quality",
        "{{ROW_WIDTH}}": translations.rowWidth ?? "Width",
        "{{ROW_HEIGHT}}": translations.rowHeight ?? "Height",
        "{{ROW_BLUR}}": translations.rowBlur ?? "Blur",
        "{{ROW_BRIGHTNESS}}": translations.rowBrightness ?? "Brightness",
        "{{ROW_CONTRAST}}": translations.rowContrast ?? "Contrast",
        "{{ROW_SHARPEN}}": translations.rowSharpen ?? "Sharpen",
        "{{ROW_ROTATE}}": translations.rowRotate ?? "Rotate",
        "{{ROW_GRAYSCALE}}": translations.rowGrayscale ?? "Grayscale",
        "{{ROW_INVERT}}": translations.rowInvert ?? "Invert",
        "{{ROW_FLIP_HORIZONTAL}}": translations.rowFlipHorizontal ?? "Flip horizontally",
        "{{ROW_FLIP_VERTICAL}}": translations.rowFlipVertical ?? "Flip vertically",
        "{{ROW_GLOBAL}}": translations.rowGlobal ?? "Global"
    };

    let output = template;
    for (const [token, value] of Object.entries(map)) {
        output = output.replaceAll(token, value);
    }

    return output;
}